/**
 * Znaczniki ewidencji KSeF i JPK dla faktur ZAKUPU — zamknięte zbiory ustawowe.
 * Wspólne dla backendu (walidator payloadu POST/PATCH, CHECK w migracji, składanie
 * atrybutów do Symfonii FK) i frontu (listy wyboru w formatce), dlatego mieszkają w
 * paczce współdzielonej, nie w kilku kopiach.
 *
 * ŚWIADOMIE NIE słownik globalny (`dictionaryCode`), mimo że katalog pól to umie.
 * Słowniki globalne są w tym repo ROZSZERZALNE PRZEZ TENANTA (mechanizm scalania
 * pozycji globalnych z lokalnymi). Wartości `purchase_evidence_marker` i
 * `jpk_document_marker` jadą DOSŁOWNIE do zamkniętego enuma Symfonii — tenant, który
 * dopisałby własną pozycję, dostałby HTTP 400 „Błąd wstępnej walidacji obiektu" bez
 * widocznej przyczyny albo, gorzej, błędne oznaczenie w ewidencji VAT. To zbiór
 * ustawowy: nikt nie ma prawa go poszerzyć. Wzorzec: `vat-tax-categories.ts`.
 *
 * Etykiety NIE są tutaj — front tłumaczy kody przez `t()` z literalnymi kluczami
 * (`tenant.purchaseInvoices.ksef.*`), bo bramka `check:i18n` nie widzi kluczy
 * budowanych dynamicznie z listy kodów.
 */

/**
 * Znacznik w ewidencji ZAKUPU. Obowiązkowy od 1.02.2026.
 *   NrKSeF — faktura odebrana z KSeF (nośnikiem jest wypełniony numer KSeF),
 *   OFF    — faktura z okresu awarii (art. 106nf ust. 1),
 *   BFK    — faktura wystawiona poza KSeF,
 *   DI     — dowód inny niż faktura.
 *
 * UWAGA — NIE USUWAJ `NrKSeF` ze zbioru „bo Symfonia go nie zna". Kod jest potrzebny
 * w ewidencji i w JPK; to warstwa wysyłki go odfiltrowuje, bo w enumie FK
 * `JPK_V7Attributes` faktycznie nie istnieje (sprawdzone w ich schemacie 2026.3) i
 * wysłanie go kończy się HTTP 400. Nośnikiem numeru jest pole `NumberKSeF`.
 */
export const PURCHASE_EVIDENCE_MARKERS = ['NrKSeF', 'OFF', 'BFK', 'DI'] as const;

/**
 * Oznaczenie dokumentu zakupu w JPK_V7.
 *   MK     — dostawca na metodzie kasowej,
 *   VAT_RR — faktura VAT RR,
 *   WEW    — dowód wewnętrzny.
 */
export const JPK_DOCUMENT_MARKERS = ['MK', 'VAT_RR', 'WEW'] as const;

/**
 * Tryb wystawienia faktury w KSeF — pole WŁASNE ewidencji, NIE jedzie do Symfonii.
 * Od trybu zależy liczenie terminów (art. 106na ust. 1, 106nda, 106nf ust. 9).
 */
export const KSEF_ISSUE_MODES = ['online', 'offline24', 'offline', 'awaryjny'] as const;

/**
 * Droga, jaką dokument dotarł do firmy. OSOBNA OŚ względem `documents.source`:
 * `source` mówi, jak rekord powstał w NASZYM systemie (ustawia backend, tylko do
 * odczytu), a to pole — skąd firma dostała dokument (wpisuje użytkownik). Faktura
 * wpisana ręcznie ze skanu ma `source='manual'` ORAZ kanał `SKAN`; jedna kolumna
 * nie udźwignie obu znaczeń.
 */
export const DOCUMENT_RECEIPT_CHANNELS = ['KSEF', 'EMAIL_PDF', 'SKAN', 'EDI', 'RECZNIE'] as const;

export type PurchaseEvidenceMarker = (typeof PURCHASE_EVIDENCE_MARKERS)[number];
export type JpkDocumentMarker = (typeof JPK_DOCUMENT_MARKERS)[number];
export type KsefIssueMode = (typeof KSEF_ISSUE_MODES)[number];
export type DocumentReceiptChannel = (typeof DOCUMENT_RECEIPT_CHANNELS)[number];

/** Case-sensitive — `NrKSeF` ma mieszaną wielkość liter i tak brzmi kod w ewidencji. */
export function isPurchaseEvidenceMarker(code: string): code is PurchaseEvidenceMarker {
  return (PURCHASE_EVIDENCE_MARKERS as readonly string[]).includes(code);
}

export function isJpkDocumentMarker(code: string): code is JpkDocumentMarker {
  return (JPK_DOCUMENT_MARKERS as readonly string[]).includes(code);
}

export function isKsefIssueMode(code: string): code is KsefIssueMode {
  return (KSEF_ISSUE_MODES as readonly string[]).includes(code);
}

export function isDocumentReceiptChannel(code: string): code is DocumentReceiptChannel {
  return (DOCUMENT_RECEIPT_CHANNELS as readonly string[]).includes(code);
}
